import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  LAMPORTS_PER_SOL,
  PublicKey,
  SystemProgram,
  Transaction,
} from "@solana/web3.js";
import { useConnection, useWallet } from "@solana/wallet-adapter-react";
import { useWalletModal } from "@solana/wallet-adapter-react-ui";
import { apiService, TokenData } from "../client/services/api.js";
import { useNetwork } from "../client/contexts/NetworkContext.js";
import type { BotConfig } from "../types/botConfig.js";

// ═══════════════════════════════════════════════════════════════════
//  B O T C O R T E X  ·  Volume Bot Engine Dashboard
//  Deep Obsidian × Synapse Purple × Warning Orange × Neon Cyan
// ═══════════════════════════════════════════════════════════════════

/* ─── STYLES ────────────────────────────────────────────────────── */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Exo+2:wght@400;600;700;800;900&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=DM+Mono:wght@400;500&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  html,body{background:#05070F;color:#C8D0F0;font-family:'Plus Jakarta Sans',sans-serif;overflow-x:hidden}
  ::-webkit-scrollbar{width:5px} ::-webkit-scrollbar-track{background:#05070F}
  ::-webkit-scrollbar-thumb{background:rgba(138,43,226,0.3);border-radius:3px}
  @keyframes slideUp{from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:translateY(0)}}
  @keyframes fadeIn{from{opacity:0}to{opacity:1}}
  @keyframes popIn{0%{transform:scale(0.8);opacity:0}80%{transform:scale(1.02)}100%{transform:scale(1);opacity:1}}
  @keyframes volPulse{0%,100%{box-shadow:0 0 18px rgba(138,43,226,0.3)}50%{box-shadow:0 0 32px rgba(138,43,226,0.6)}}
  @keyframes blobA{0%,100%{border-radius:60% 40% 30% 70%/60% 30% 70% 40%}50%{border-radius:30% 60% 70% 40%/50% 60% 30% 60%}}
  @keyframes blobB{0%,100%{border-radius:40% 60% 50% 50%/50% 40% 60% 50%}50%{border-radius:60% 40% 30% 70%/60% 30% 70% 40%}}
  @keyframes scanH{0%{transform:translateY(-10px)}100%{transform:translateY(100vh)}}
  @keyframes tickL{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}
  @keyframes marquee{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}
  @keyframes gradShift{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}
  @keyframes ping{0%{transform:scale(1);opacity:1}75%,100%{transform:scale(2.2);opacity:0}}
  @keyframes tradeIn{0%{opacity:0;transform:translateX(-12px)}100%{opacity:1;transform:translateX(0)}}
  @keyframes heartbeat{0%,100%{transform:scale(1)}50%{transform:scale(1.04)}}
  @keyframes shimmer{0%{opacity:0.4}50%{opacity:0.9}100%{opacity:0.4}}
  @keyframes lockPulse{0%,100%{transform:scale(1)}50%{transform:scale(1.04);box-shadow:0 0 40px rgba(138,43,226,0.5)}}
  @keyframes lockRing{0%{transform:scale(0.9);opacity:0.5}100%{transform:scale(1.7);opacity:0}}
  @keyframes lockFade{from{opacity:0}to{opacity:1}}
  @keyframes walletIn{from{opacity:0;transform:scale(0.9) translateY(16px)}to{opacity:1;transform:scale(1) translateY(0)}}
  @keyframes orb1{0%,100%{transform:translate(0,0)}50%{transform:translate(28px,-18px)}}
  @keyframes orb2{0%,100%{transform:translate(0,0)}50%{transform:translate(-22px,26px)}}
  .exo{font-family:'Exo 2',sans-serif!important}
  .mono{font-family:'DM Mono',monospace!important}
  .vol-text{background:linear-gradient(135deg,#8A2BE2 0%,#FF4500 50%,#00F0FF 100%);background-size:200%;-webkit-background-clip:text;-webkit-text-fill-color:transparent;animation:gradShift 4s ease infinite}
  
  .launch-text{color:#FFFFFF;text-shadow: 0 0 10px rgba(138, 43, 226, 0.8), 0 0 20px rgba(138, 43, 226, 0.4);}
  .nuke-text{color:#FF4500;text-shadow: 0 0 10px rgba(255, 69, 0, 0.8), 0 0 20px rgba(255, 69, 0, 0.4);}
  .earn-text{color:#00F0FF;text-shadow: 0 0 10px rgba(0, 240, 255, 0.8), 0 0 20px rgba(0, 240, 255, 0.4);}

  .btn-vol{background:linear-gradient(135deg,#8A2BE2,#5c18a0);color:#fff;border:none;cursor:pointer;font-family:inherit;font-weight:800;border-radius:14px;padding:13px 28px;transition:all 0.25s;position:relative;overflow:hidden}
  .btn-vol:hover{transform:translateY(-2px) scale(1.02);box-shadow:0 10px 32px rgba(138,43,226,0.5)}
  .btn-vol:active{transform:scale(0.97)}
  .btn-buy{background:linear-gradient(135deg,#00F0FF,#00c2cf);color:#05070F;border:none;cursor:pointer;font-family:inherit;font-weight:800;border-radius:14px;padding:13px 28px;transition:all 0.25s}
  .btn-buy:hover{transform:translateY(-2px);box-shadow:0 10px 32px rgba(0,240,255,0.4)}
  .btn-ghost{background:transparent;border:1.5px solid rgba(138,43,226,0.22);color:#8A2BE2;cursor:pointer;font-family:inherit;font-weight:700;border-radius:12px;padding:10px 22px;transition:all 0.25s}
  .btn-ghost:hover{border-color:#8A2BE2;color:#b266ff;background:rgba(138,43,226,0.07)}
  .card{background:#080B18;border:1px solid rgba(138,43,226,0.1);border-radius:18px;transition:all 0.3s}
  .card:hover{border-color:rgba(138,43,226,0.28);box-shadow:0 16px 48px rgba(0,0,0,0.5)}
  .card-glow{animation:volPulse 3s ease-in-out infinite}
  .inp{background:rgba(255,255,255,0.03);border:1.5px solid rgba(138,43,226,0.14);border-radius:12px;color:#C8D0F0;font-family:inherit;font-size:14px;outline:none;padding:12px 16px;width:100%;transition:all 0.3s}
  .inp:focus{border-color:#8A2BE2;background:rgba(138,43,226,0.05)}
  .inp::placeholder{color:#2A2060}
  .inp-mono{background:rgba(255,255,255,0.03);border:1.5px solid rgba(0,240,255,0.14);border-radius:12px;color:#C8D0F0;font-family:'DM Mono',monospace;font-size:13px;outline:none;padding:12px 16px;width:100%;transition:all 0.3s}
  .inp-mono:focus{border-color:#00F0FF;background:rgba(0,240,255,0.04)}
  .inp-mono::placeholder{color:#1E1A50}
  .tag{display:inline-flex;align-items:center;gap:5px;padding:3px 10px;border-radius:999px;font-size:10.5px;font-weight:800;letter-spacing:0.5px;text-transform:uppercase}
  .tg-vol{background:rgba(138,43,226,0.14);color:#b266ff;border:1px solid rgba(138,43,226,0.28)}
  .tg-cyan{background:rgba(0,240,255,0.1);color:#00c2cf;border:1px solid rgba(0,240,255,0.22)}
  .tg-green{background:rgba(0,255,157,0.1);color:#00EE8D;border:1px solid rgba(0,255,157,0.22)}
  .tg-amber{background:rgba(255,69,0,0.12);color:#FF4500;border:1px solid rgba(255,69,0,0.22)}
  .tg-red{background:rgba(255,45,85,0.1);color:#FF5577;border:1px solid rgba(255,45,85,0.2)}
  .tg-pur{background:rgba(138,43,226,0.1);color:#b266ff;border:1px solid rgba(138,43,226,0.22)}
  .tg-live{background:rgba(0,255,157,0.12);color:#00FF9D;border:1px solid rgba(0,255,157,0.28);animation:volPulse 2.2s ease-in-out infinite}
  .seg{padding:8px 14px;border-radius:9px;cursor:pointer;font-size:12.5px;font-weight:700;font-family:inherit;transition:all 0.2s;border:1.5px solid rgba(138,43,226,0.1);background:transparent;color:#4A3878}
  .seg:hover{border-color:rgba(138,43,226,0.3);color:#b266ff}
  .seg.on{background:rgba(138,43,226,0.12);border-color:rgba(138,43,226,0.38);color:#b266ff}
  .seg-c{padding:8px 14px;border-radius:9px;cursor:pointer;font-size:12.5px;font-weight:700;font-family:inherit;transition:all 0.2s;border:1.5px solid rgba(0,240,255,0.1);background:transparent;color:#1A3A4A}
  .seg-c:hover{border-color:rgba(0,240,255,0.3);color:#00c2cf}
  .seg-c.on{background:rgba(0,240,255,0.1);border-color:rgba(0,240,255,0.35);color:#00F0FF}
  .toggle{width:40px;height:22px;border-radius:999px;border:none;cursor:pointer;transition:all 0.3s;position:relative;flex-shrink:0}
  .toggle::after{content:'';position:absolute;top:3px;left:3px;width:16px;height:16px;border-radius:50%;background:#fff;transition:all 0.3s}
  .toggle.on{background:linear-gradient(135deg,#8A2BE2,#5c18a0)}
  .toggle.on::after{left:21px}
  .toggle.off{background:#120E2E}
  .live-row{animation:tradeIn 0.3s ease}
  input[type=range]{accent-color:#8A2BE2;cursor:pointer;width:100%}
  input[type=range].cyan-range{accent-color:#00F0FF}
  .marquee-content{display:flex;align-items:center;gap:40px;padding-right:40px;width:max-content;animation:marquee 35s linear infinite}
  .marquee-content:hover{animation-play-state:paused}
  @media(max-width:680px){.hsm{display:none!important}.g2{grid-template-columns:1fr!important}.marquee-content{gap:24px;padding-right:24px}}
`;

function StyleInjector() {
  useEffect(() => {
    const el = document.createElement("style");
    el.textContent = CSS;
    document.head.appendChild(el);
    return () => {
      try {
        document.head.removeChild(el);
      } catch (_) {}
    };
  }, []);
  return null;
}

/* ─── CONSTANTS ─────────────────────────────────────────────────── */
const DEFAULT_CFG = {
  solBudget: 0.7,
  duration: 30,
  walletCount: 20,
  buyPressure: 75,
  freqPreset: "normal" as "slow" | "normal" | "fast" | "blast",
  jitoTip: "med" as "low" | "med" | "high" | "ultra",
  trendingMode: true,
  antiBot: true,
  splitOrders: true,
  minTrade: 0.01,
  maxTrade: 0.05,
};

const FREQ_LABELS = { slow: "~1/min", normal: "~3/min", fast: "~8/min", blast: "~20/min" };
const FREQ_TPM = { slow: 1, normal: 3, fast: 8, blast: 20 };
const JITO_LABELS = { low: "0.0001 SOL", med: "0.0003 SOL", high: "0.001 SOL", ultra: "0.005 SOL" };

const MOCK_TOKENS: Record<string, any> = {
  "DezXAZ8z7PnrnRJjz3wXBoRgixCa6xjnB7YaB1pPB263": {
    mint: "DezXAZ8z7PnrnRJjz3wXBoRgixCa6xjnB7YaB1pPB263",
    name: "Bonk",
    symbol: "BONK",
    logo: "🐕",
    price: 0.0000152,
    mcap: 1050000,
    vol24h: 42000,
    change24: 12.4,
    holders: 412000,
  },
  "EKpQGSJtjMFqKZ9KQanSqYXRcF8fBopzLHYxdM65zcjm": {
    mint: "EKpQGSJtjMFqKZ9KQanSqYXRcF8fBopzLHYxdM65zcjm",
    name: "dogwifhat",
    symbol: "WIF",
    logo: "🎩",
    price: 1.84,
    mcap: 1840000,
    vol24h: 88000,
    change24: 8.7,
    holders: 95000,
  },
  "7GCihgDB8fe6KNjn2MYtkzZcRjQy3t9GHdC8uHYmW2hr": {
    mint: "7GCihgDB8fe6KNjn2MYtkzZcRjQy3t9GHdC8uHYmW2hr",
    name: "Popcat",
    symbol: "POPCAT",
    logo: "😸",
    price: 0.52,
    mcap: 520000,
    vol24h: 22000,
    change24: -3.2,
    holders: 58000,
  },
};

const FAQS = [
  { q: "How does BotCortex actually work?", a: "The bot generates ephemeral Solana wallets, funds them with fractional SOL, then executes buy/sell transactions through Jupiter Ultra Swap. All transactions are submitted via Jito bundles (MEV-protected) and broadcast through Helius staked RPC. Trade sizes, timing, and wallet counts are randomized to appear organic." },
  { q: "Why does volume matter for my meme coin?", a: "Volume is the most important metric for meme coin visibility. Dexscreener, Birdeye, and DEXTools rank tokens by volume. Higher volume = higher ranking = more real buyers. Volume also signals market health and is required to appear on trading terminal trending pages." },
  { q: "Will this show up as bot activity?", a: "Our Forge Randomizer and Anti-Fingerprint Cloak defeat the pattern-recognition algorithms used by these platforms. Random wallet sizes, Poisson-distributed timing, variable Jito tips, and wallet funding variance make all transactions appear as natural, independent trades." },
  { q: "Basic (0.7 SOL) vs Custom — what's the difference?", a: "Basic Forge is a pre-configured one-click package: 20 wallets, 30 minutes, normal frequency, trending mode ON. Custom lets you configure every parameter — SOL budget (0.5–50 SOL), duration, wallet count, buy pressure, trade frequency, and Jito tip tier." },
  { q: "How long to appear on Dexscreener trending?", a: "With Trending Ignition enabled, you typically appear within 8–15 minutes for tokens under $500K market cap. For larger tokens, 20–45 minutes with 5+ SOL is typical. Birdeye trending usually follows within 30 minutes of Dexscreener." },
  { q: "Is my SOL safe?", a: "Your SOL is only spent on actual on-chain swap transactions. Jito bundle protection ensures no partial execution. The bot is non-custodial: you sign the initial funding transaction and all activity is verifiable on-chain." },
];

/* ─── HELPERS ───────────────────────────────────────────────────── */
function rng(min: number, max: number) {
  return min + Math.random() * (max - min);
}
function randBase58(len: number) {
  const c = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz123456789";
  return Array.from({ length: len }, () => c[Math.floor(Math.random() * c.length)]).join("");
}
function randAddr() {
  return randBase58(44);
}
function randHash() {
  const h = "0123456789abcdef";
  return Array.from({ length: 64 }, () => h[Math.floor(Math.random() * h.length)]).join("");
}

function calcEst(cfg: typeof DEFAULT_CFG) {
  const tpm = FREQ_TPM[cfg.freqPreset];
  const total = Math.round(tpm * cfg.duration);
  const avgTrade = (cfg.minTrade + cfg.maxTrade) / 2;
  return {
    trades: total,
    buys: Math.round(total * cfg.buyPressure / 100),
    sells: Math.round(total * (100 - cfg.buyPressure) / 100),
    volSOL: avgTrade * total,
    tpw: Math.max(1, Math.floor(total / cfg.walletCount)),
  };
}

const PACKAGE_BASIC_SOL = 0.7;
const PACKAGE_BASIC_TYPE = "0.5 SOL" as const;
const PACKAGE_CUSTOM_TYPE = "4.5 SOL" as const;
const EST_SHOW_MULT = 15;
const SOL_PRICE_USD = 145;
const ENGINE_PACE = 1.4;

function toBotConfig(cfg: typeof DEFAULT_CFG): BotConfig {
  return {
    durationMinutes: cfg.duration,
    walletCount: cfg.walletCount,
    buyPressure: cfg.buyPressure,
    freqPreset: cfg.freqPreset,
    jitoTip: cfg.jitoTip,
  };
}

function getPackagePayment(plan: "basic" | "custom", cfg: typeof DEFAULT_CFG) {
  if (plan === "basic") {
    return { packageType: PACKAGE_BASIC_TYPE, paymentSol: PACKAGE_BASIC_SOL };
  }
  return { packageType: PACKAGE_CUSTOM_TYPE, paymentSol: cfg.solBudget };
}

function fmtWallet(addr: string) {
  return addr.slice(0, 4) + "..." + addr.slice(-4);
}

function fmtUsd(n: number | null | undefined) {
  if (n == null || Number.isNaN(n)) return "—";
  if (n >= 1e6) return "$" + (n / 1e6).toFixed(2) + "M";
  if (n >= 1e3) return "$" + (n / 1e3).toFixed(1) + "K";
  return "$" + n.toFixed(2);
}
function fmtMcap(n: number | null | undefined) {
  if (n == null || Number.isNaN(n)) return "—";
  if (n >= 1e6) return "$" + (n / 1e6).toFixed(2) + "M";
  return "$" + (n / 1e3).toFixed(1) + "K";
}
function fmtCount(n: number | null | undefined) {
  if (n == null || Number.isNaN(n)) return "—";
  return n.toLocaleString();
}
function fmtPct(n: number) {
  if (!Number.isFinite(n)) return "+0.0%";
  return (n >= 0 ? "+" : "") + n.toFixed(1) + "%";
}
function fmtAddr(s: string) {
  return s.slice(0, 4) + "..." + s.slice(-4);
}
function fmtTime(s: number) {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return m + ":" + String(sec).padStart(2, "0");
}
function fmtPrice(p: number) {
  if (!Number.isFinite(p) || p <= 0) return "$0.0000";
  return "$" + p.toFixed(p < 0.001 ? 8 : 4);
}

function useDraggablePanel(initial: { x: number; y: number }, panelWidth = 320) {
  const [pos, setPos] = useState(initial);
  const dragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null);

  const clampPos = useCallback(function (x: number, y: number) {
    if (typeof window === "undefined") return { x, y };
    const w = Math.min(panelWidth, window.innerWidth - 24);
    return {
      x: Math.max(8, Math.min(window.innerWidth - w - 8, x)),
      y: Math.max(8, Math.min(window.innerHeight - 96, y)),
    };
  }, [panelWidth]);

  useEffect(function () {
    function onResize() {
      setPos(function (p) { return clampPos(p.x, p.y); });
    }
    window.addEventListener("resize", onResize);
    return function () { window.removeEventListener("resize", onResize); };
  }, [clampPos]);

  const onPointerDown = useCallback(function (e: React.PointerEvent<HTMLElement>) {
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origX: pos.x,
      origY: pos.y,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  }, [pos.x, pos.y]);

  const onPointerMove = useCallback(function (e: React.PointerEvent<HTMLElement>) {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setPos(clampPos(dragRef.current.origX + dx, dragRef.current.origY + dy));
  }, [clampPos]);

  const onPointerUp = useCallback(function (e: React.PointerEvent<HTMLElement>) {
    dragRef.current = null;
    e.currentTarget.releasePointerCapture(e.pointerId);
  }, []);

  return { pos, onPointerDown, onPointerMove, onPointerUp };
}

interface LiveActivityRow {
  id: string;
  kind: "system" | "trade";
  message?: string;
  wallet?: string;
  dir?: "buy" | "sell";
  amount?: number;
  usdValue?: number;
  hash?: string;
  ms?: number;
}

function useLiveEngine(cfg: typeof DEFAULT_CFG, token: TokenData) {
  const basePrice = token.price > 0 ? token.price : 0.0000152;
  const [price, setPrice] = useState(basePrice);
  const [activity, setActivity] = useState<LiveActivityRow[]>([]);
  const [engineReady, setEngineReady] = useState(false);
  const [stats, setStats] = useState({
    totalVolume: 0,
    trades: 0,
    buys: 0,
    sells: 0,
    wallets: 0,
    elapsed: 0,
    solSpent: 0,
    trending: false,
    avgConfirm: 325,
  });

  const walletCount = cfg.walletCount || 20;
  const buyPressure = cfg.buyPressure ?? 75;
  const freqPreset = cfg.freqPreset || "normal";

  useEffect(() => {
    const elapsedTimer = setInterval(() => {
      setStats((s) => Object.assign({}, s, { elapsed: s.elapsed + 1 }));
    }, 1000);
    return () => clearInterval(elapsedTimer);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const pushSystem = (message: string) => {
      if (cancelled) return;
      const row: LiveActivityRow = {
        id: "sys-" + Date.now() + "-" + Math.random(),
        kind: "system",
        message,
      };
      setActivity((rows) => [row, ...rows].slice(0, 80));
    };

    const timers: ReturnType<typeof setTimeout>[] = [];
    const schedule = (ms: number, fn: () => void) => {
      timers.push(setTimeout(fn, Math.round(ms / ENGINE_PACE)));
    };

    pushSystem("Initializing volume engine...");
    schedule(800, () => pushSystem("Worker wallet verified · node sync OK"));
    schedule(1600, () => pushSystem("Connecting to Jito block engine..."));
    schedule(2400, () => pushSystem("Spawning burner wallet cluster (" + walletCount + " nodes)"));
    schedule(3200, () => {
      pushSystem("Funding burner wallets · batch transfer initiated");
      setStats((s) => Object.assign({}, s, { wallets: Math.max(1, Math.floor(walletCount / 3)) }));
    });
    schedule(4200, () => {
      pushSystem("Burner wallets funded · " + walletCount + "/" + walletCount + " active");
      setStats((s) => Object.assign({}, s, { wallets: walletCount }));
    });
    schedule(5000, () => pushSystem("Jupiter router attached · pool route locked"));
    schedule(5800, () => pushSystem("Buy pressure " + buyPressure + "% · " + FREQ_LABELS[freqPreset]));
    schedule(6500, () => {
      pushSystem("Submitting first Jito bundle...");
      if (!cancelled) setEngineReady(true);
    });

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
      setEngineReady(false);
    };
  }, [walletCount, buyPressure, freqPreset]);

  useEffect(() => {
    if (!engineReady) return;

    const tpm = FREQ_TPM[freqPreset];
    const minBuysBeforeSell = 2;
    let buyCount = 0;

    const tick = () => {
      const dir: "buy" | "sell" = buyCount < minBuysBeforeSell
        ? "buy"
        : (Math.random() * 100 < buyPressure ? "buy" : "sell");
      if (dir === "buy") buyCount += 1;
      const amount = cfg.minTrade + Math.random() * (cfg.maxTrade - cfg.minTrade);
      const hash = randBase58(88);
      const wallet = randBase58(44);
      const usdValue = amount * SOL_PRICE_USD;
      const ms = Math.round(280 + Math.random() * 120);

      setActivity((rows) => {
        const row: LiveActivityRow = {
          id: hash + "-" + Date.now(),
          kind: "trade",
          wallet,
          dir,
          amount,
          usdValue,
          hash,
          ms,
        };
        return [row, ...rows].slice(0, 80);
      });

      setStats((s) => {
        const trades = s.trades + 1;
        const totalVolume = s.totalVolume + usdValue;
        return Object.assign({}, s, {
          trades,
          buys: s.buys + (dir === "buy" ? 1 : 0),
          sells: s.sells + (dir === "sell" ? 1 : 0),
          totalVolume,
          solSpent: s.solSpent + 0.00025 + Math.random() * 0.00035,
          avgConfirm: ms,
          wallets: walletCount,
          trending: totalVolume > 1200 || s.elapsed > 300,
        });
      });

      setPrice((p) => {
        const drift = dir === "buy" ? 1.00015 : 0.99985;
        const jitter = 1 + (Math.random() * 0.0003 - 0.00015);
        return Math.max(p * drift * jitter, basePrice * 0.92);
      });
    };

    tick();
    const baseIntervalMs = Math.max(1400, Math.round(60000 / tpm / ENGINE_PACE));
    const tradeTimer = setInterval(tick, baseIntervalMs);

    return () => clearInterval(tradeTimer);
  }, [engineReady, walletCount, buyPressure, freqPreset, cfg.minTrade, cfg.maxTrade, basePrice]);

  return { price, activity, stats, basePrice };
}

/* ─── SUBCOMPONENTS ─────────────────────────────────────────────── */
function BgScene() {
  return (
    <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0, overflow: "hidden" }}>
      <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(rgba(138,43,226,0.05) 1px,transparent 1px)", backgroundSize: "32px 32px" }} />
      <div style={{ position: "absolute", top: "-5%", left: "-8%", width: 700, height: 700, background: "radial-gradient(ellipse,rgba(138,43,226,0.09),transparent 70%)", filter: "blur(60px)", animation: "blobA 16s ease-in-out infinite" }} />
      <div style={{ position: "absolute", bottom: "-5%", right: "-5%", width: 650, height: 650, background: "radial-gradient(ellipse,rgba(0,240,255,0.07),transparent 70%)", filter: "blur(65px)", animation: "blobB 20s ease-in-out infinite reverse" }} />
      <div style={{ position: "absolute", left: 0, right: 0, height: 1, background: "linear-gradient(90deg,transparent,rgba(138,43,226,0.06),transparent)", animation: "scanH 14s linear infinite" }} />
    </div>
  );
}

function Logo({ size = 28, showSub = false }: { size?: number; showSub?: boolean }) {
  const sz = size;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
      {/* Circuit Synapse Brain Logo */}
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 128 128" width={sz * 1.3} height={sz * 1.3} style={{ filter: 'drop-shadow(0 0 8px rgba(138,43,226,0.6))', flexShrink: 0 }}>
        <defs>
          <linearGradient id="logoGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#8A2BE2" />
            <stop offset="50%" stopColor="#FF4500" />
            <stop offset="100%" stopColor="#00F0FF" />
          </linearGradient>
        </defs>
        <circle cx="64" cy="64" r="54" fill="none" stroke="url(#logoGrad)" strokeWidth="3" opacity="0.25" />
        <g>
          {/* Right side of brain network */}
          <path d="M64 24C78 24 90 32 94 44C98 42 104 42 106 46C108 50 106 58 100 62C104 68 104 76 98 82C98 88 92 94 86 94C82 100 74 104 64 104" fill="none" stroke="url(#logoGrad)" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
          {/* Left side of brain network */}
          <path d="M64 24C50 24 38 32 34 44C30 42 24 42 22 46C20 50 22 58 28 62C24 68 24 76 30 82C30 88 36 94 42 94C46 100 54 104 64 104" fill="none" stroke="url(#logoGrad)" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
          {/* Central Stem */}
          <path d="M64 24V104" fill="none" stroke="#00F0FF" strokeWidth="3.5" strokeLinecap="round" />
          {/* Synaptic nodes */}
          <circle cx="64" cy="40" r="4.5" fill="#00F0FF" />
          <circle cx="64" cy="64" r="4.5" fill="#FF4500" />
          <circle cx="64" cy="88" r="4.5" fill="#8A2BE2" />
          <circle cx="44" cy="54" r="3.5" fill="#8A2BE2" />
          <circle cx="84" cy="54" r="3.5" fill="#FF4500" />
          <circle cx="44" cy="74" r="3.5" fill="#00F0FF" />
          <circle cx="84" cy="74" r="3.5" fill="#8A2BE2" />
        </g>
      </svg>
      <div>
        <div className="exo" style={{ fontSize: sz * 0.75, fontWeight: 900, letterSpacing: "-0.3px", color: "#C8D0F0", lineHeight: 1.1 }}>
          BOT<span className="vol-text">CORTEX</span>
        </div>
        {showSub && (
          <div style={{ fontSize: 9, fontWeight: 800, color: "#5a4392", letterSpacing: 2, textTransform: "uppercase", marginTop: 1 }}>Volume Engine</div>
        )}
      </div>
    </div>
  );
}

function WalletMenuButton() {
  const { publicKey, connected, disconnect } = useWallet();
  const { setVisible } = useWalletModal();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(function () {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return function () {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  if (!connected || !publicKey) {
    return (
      <button
        className="btn-vol"
        style={{ padding: "8px 18px", fontSize: 12.5, borderRadius: 10 }}
        onClick={function () { setVisible(true); }}
      >
        👻 Connect Wallet
      </button>
    );
  }

  const address = publicKey.toBase58();

  async function handleDisconnect() {
    setOpen(false);
    await disconnect();
  }

  return (
    <div ref={menuRef} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={function () { setOpen(function (v) { return !v; }); }}
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          background: "rgba(138,43,226,0.08)",
          border: "1px solid rgba(138,43,226,0.2)",
          borderRadius: 10,
          padding: "6px 13px",
          cursor: "pointer",
          fontFamily: "inherit",
        }}
      >
        <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#00FF9D", boxShadow: "0 0 6px #00FF9D", display: "inline-block" }} />
        <span className="mono" style={{ fontSize: 12, color: "#b266ff", fontWeight: 700 }}>{fmtWallet(address)}</span>
        <span style={{ fontSize: 9, color: "#5a4392" }}>{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            right: 0,
            minWidth: 168,
            background: "#0A0B1A",
            border: "1px solid rgba(138,43,226,0.25)",
            borderRadius: 10,
            padding: 6,
            zIndex: 100,
            boxShadow: "0 8px 24px rgba(0,0,0,0.45)",
          }}
        >
          <button
            type="button"
            onClick={handleDisconnect}
            style={{
              width: "100%",
              background: "rgba(255,45,85,0.08)",
              border: "1px solid rgba(255,45,85,0.2)",
              borderRadius: 8,
              padding: "8px 12px",
              cursor: "pointer",
              color: "#FF5577",
              fontFamily: "inherit",
              fontWeight: 700,
              fontSize: 12,
            }}
          >
            Disconnect
          </button>
        </div>
      )}
    </div>
  );
}

/* ─── WALLET LOCK OVERLAY ───────────────────────────────────────── */
function SystemStateBadge() {
  return (
    <div
      className="hsm system-state-badge"
      aria-label="System state active"
      style={{
        position: "relative",
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 3,
        padding: "7px 18px 8px",
        minWidth: 104,
        borderRadius: 999,
        background:
          "linear-gradient(180deg, rgba(138,43,226,0.11) 0%, rgba(8,11,24,0.92) 55%, rgba(5,7,15,0.98) 100%)",
        border: "1px solid rgba(138,43,226,0.24)",
        boxShadow:
          "inset 0 1px 0 rgba(255,255,255,0.04), inset 0 0 12px rgba(0,255,157,0.03), 0 4px 18px rgba(0,0,0,0.28), 0 0 24px rgba(138,43,226,0.1)",
        backdropFilter: "blur(10px)",
        overflow: "hidden",
      }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(ellipse 80% 60% at 50% 0%, rgba(0,255,157,0.07) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />
      <span
        style={{
          position: "relative",
          fontSize: 7.5,
          color: "#6B5594",
          letterSpacing: "0.16em",
          fontWeight: 800,
          textTransform: "uppercase",
          lineHeight: 1,
        }}
      >
        System State
      </span>
      <span
        style={{
          position: "relative",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          lineHeight: 1,
        }}
      >
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: "#00FF9D",
            boxShadow: "0 0 8px rgba(0,255,157,0.85)",
            animation: "ping 1.8s ease-in-out infinite",
            flexShrink: 0,
          }}
        />
        <span
          className="exo"
          style={{
            fontSize: 11.5,
            fontWeight: 900,
            letterSpacing: "0.1em",
            color: "#00FF9D",
            textShadow: "0 0 14px rgba(0,255,157,0.42)",
            textTransform: "uppercase",
          }}
        >
          Active
        </span>
      </span>
    </div>
  );
}

function NetworkStatusDot() {
  const { isDevnet } = useNetwork();

  return (
    <span
      title={isDevnet ? "Devnet" : "Mainnet"}
      aria-label={isDevnet ? "Devnet network" : "Mainnet network"}
      style={{
        width: 10,
        height: 10,
        borderRadius: "50%",
        background: isDevnet ? "#FF4500" : "#00FF9D",
        boxShadow: isDevnet
          ? "0 0 8px rgba(255,69,0,0.45)"
          : "0 0 8px rgba(0,255,157,0.45)",
        flexShrink: 0,
        display: "inline-block",
      }}
    />
  );
}

function MarqueeBanner() {
  const [items, setItems] = useState([
    { token: "$BONK", price: "$0.000015", change: 12.4 },
    { token: "$WIF", price: "$1.84", change: 8.7 },
    { token: "$POPCAT", price: "$0.52", change: -3.2 },
    { token: "$SOL", price: "$145.20", change: 4.5 },
  ]);

  useEffect(() => {
    const t = setInterval(() => {
      setItems((arr) => {
        return arr.map((item) => {
          const varPct = 1 + (Math.random() * 0.01 - 0.005);
          return Object.assign({}, item, {
            price: item.token === "$SOL" ? (parseFloat(item.price.slice(1)) * varPct).toFixed(2) : item.price,
            change: item.change + (Math.random() * 0.4 - 0.2),
          });
        });
      });
    }, 4000);
    return () => clearInterval(t);
  }, []);

  return (
    <div style={{ height: 28, background: "rgba(8,11,24,0.97)", borderBottom: "1px solid rgba(138,43,226,0.08)", overflow: "hidden", display: "flex", alignItems: "center" }}>
      <div className="marquee-content" style={{ display: "flex", alignItems: "center" }}>
        {Array.from({ length: 4 }).flatMap((_, i) =>
          items.map((it, idx) => (
            <div key={`${it.token}-${i}-${idx}`} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, fontFamily: "'DM Mono',monospace" }}>
              <span style={{ color: "#5a4392" }}>{it.token}</span>
              <span style={{ color: "#C8D0F0" }}>{it.token === "$SOL" ? `$${it.price}` : it.price}</span>
              <span style={{ color: it.change >= 0 ? "#00FF9D" : "#FF5577" }}>{fmtPct(it.change)}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function WalletLock({ onConnect }: { onConnect: () => void }) {
  return (
    <div style={{ position: "absolute", inset: 0, zIndex: 40, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", animation: "lockFade 0.5s ease forwards", backdropFilter: "blur(22px)", background: "rgba(5,7,15,0.75)", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: "20%", left: "15%", width: 400, height: 400, background: "radial-gradient(circle,rgba(138,43,226,0.12),transparent 70%)", filter: "blur(60px)", animation: "orb1 12s ease-in-out infinite", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: "15%", right: "10%", width: 350, height: 350, background: "radial-gradient(circle,rgba(0,240,255,0.08),transparent 70%)", filter: "blur(60px)", animation: "orb2 15s ease-in-out infinite", pointerEvents: "none" }} />
      <div style={{ textAlign: "center", padding: "50px 40px", maxWidth: 460, position: "relative", zIndex: 1 }}>
        <div style={{ position: "relative", display: "inline-block", marginBottom: 30 }}>
          <div style={{ position: "absolute", top: -24, left: -24, right: -24, bottom: -24, borderRadius: "50%", border: "2px solid rgba(138,43,226,0.15)", animation: "lockRing 2.2s ease-out infinite" }} />
          <div style={{ position: "absolute", top: -24, left: -24, right: -24, bottom: -24, borderRadius: "50%", border: "2px solid rgba(138,43,226,0.1)", animationName: "lockRing", animationDuration: "2.2s", animationTimingFunction: "ease-out", animationIterationCount: "infinite", animationDelay: "0.7s" }} />
          <div style={{ position: "absolute", top: -24, left: -24, right: -24, bottom: -24, borderRadius: "50%", border: "2px solid rgba(138,43,226,0.06)", animationName: "lockRing", animationDuration: "2.2s", animationTimingFunction: "ease-out", animationIterationCount: "infinite", animationDelay: "1.4s" }} />
          <div style={{ width: 96, height: 96, borderRadius: "50%", background: "linear-gradient(135deg,#8A2BE2,#5c18a0)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 42, animation: "lockPulse 2.8s ease-in-out infinite", boxShadow: "0 20px 60px rgba(138,43,226,0.35)", position: "relative" }}>
            🧠
          </div>
        </div>
        <div className="exo" style={{ fontSize: 32, fontWeight: 900, marginBottom: 6, color: "#C8D0F0" }}>
          BOT<span className="vol-text">CORTEX</span>
        </div>
        <div style={{ fontSize: 11, fontWeight: 800, color: "#5a4392", letterSpacing: 2.5, textTransform: "uppercase", marginBottom: 24 }}>Solana Volume Engine</div>
        <p style={{ fontSize: 14, color: "#7A6A9B", lineHeight: 1.85, marginBottom: 32, fontWeight: 500 }}>
          Connect your Solana wallet to access the{" "}
          <strong style={{ color: "#b266ff" }}>Volume Control Center</strong> and boost your token performance.
        </p>
        <button className="btn-vol" style={{ padding: "16px 44px", fontSize: 16, borderRadius: 16, width: "100%", animation: "heartbeat 2.2s ease-in-out infinite" }} onClick={onConnect}>
          <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
            <span>👻</span>
            <span>Connect Wallet to Access</span>
          </span>
        </button>
        <div style={{ marginTop: 18, display: "flex", justifyContent: "center", gap: 18, flexWrap: "wrap" }}>
          {["Phantom", "Solflare", "Backpack", "Coinbase"].map(function (w) {
            return <span key={w} style={{ fontSize: 11, color: "#2A2060", fontWeight: 700 }}>{w}</span>;
          })}
        </div>
        <div style={{ marginTop: 14, fontSize: 11, color: "#1E1A50", fontWeight: 600, lineHeight: 1.7 }}>
          🔑 Non-custodial · No registration · Quick Setup
        </div>
      </div>
    </div>
  );
}

/* ─── TOKEN WIDGET ──────────────────────────────────────────────── */
interface TokenWidgetProps {
  token: TokenData | null;
  loading: boolean;
  error: string;
  mint: string;
  onMint: (m: string) => void;
  onFetch: () => void;
}

function TokenWidget({ token, loading, error, mint, onMint, onFetch }: TokenWidgetProps) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 800, color: "#5a4392", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.8 }}>🪙 Token Mint Address</div>
      <div style={{ display: "flex", gap: 8 }}>
        <div style={{ position: "relative", flex: 1 }}>
          <input
            className="inp-mono"
            style={{ fontSize: 14, padding: "13px 16px", borderRadius: 13 }}
            value={mint}
            onChange={function (e) { onMint(e.target.value); }}
            placeholder="Paste Solana token mint address…"
            onKeyDown={function (e) { if (e.key === "Enter") onFetch(); }}
          />
          {mint.length > 0 && !loading && !token && (
            <div style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", fontSize: 11, color: "#1E1A50", fontWeight: 700 }}>{mint.length}</div>
          )}
        </div>
        <button className="btn-vol" style={{ padding: "0 18px", borderRadius: 12, fontSize: 13, flexShrink: 0 }} onClick={onFetch} disabled={loading}>
          <span>{loading ? "⏳" : "🔍 Fetch"}</span>
        </button>
      </div>

      <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 10.5, color: "#2A2060", fontWeight: 700, alignSelf: "center" }}>Quick test:</span>
        {Object.values(MOCK_TOKENS).map(function (t) {
          return (
            <button key={t.mint} onClick={function () { onMint(t.mint); }}
              style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(138,43,226,0.1)", borderRadius: 6, padding: "3px 10px", cursor: "pointer", fontSize: 11, fontWeight: 700, color: "#7A6A9B", fontFamily: "inherit", transition: "all 0.2s" }}>
              {t.logo} {t.symbol}
            </button>
          );
        })}
      </div>

      {error && (
        <div style={{ background: "rgba(255,45,85,0.07)", border: "1px solid rgba(255,45,85,0.2)", borderRadius: 10, padding: "10px 14px", marginTop: 10, fontSize: 12.5, color: "#FF5577", fontWeight: 600, animation: "fadeIn 0.3s ease" }}>
          ⚠️ {error}
        </div>
      )}

      {loading && (
        <div style={{ background: "rgba(0,240,255,0.04)", border: "1px solid rgba(0,240,255,0.1)", borderRadius: 14, padding: "16px 18px", marginTop: 12, animation: "fadeIn 0.3s ease" }}>
          <div style={{ display: "flex", gap: 13, alignItems: "center" }}>
            <div style={{ width: 44, height: 44, borderRadius: "50%", background: "rgba(255,255,255,0.06)", animation: "shimmer 1.2s ease infinite" }} />
            <div style={{ flex: 1 }}>
              <div style={{ height: 13, background: "rgba(255,255,255,0.05)", borderRadius: 6, marginBottom: 7, animation: "shimmer 1.2s ease infinite", width: "60%" }} />
              <div style={{ height: 10, background: "rgba(255,255,255,0.03)", borderRadius: 5, animation: "shimmer 1.2s ease infinite", width: "40%" }} />
            </div>
          </div>
          <div style={{ marginTop: 10, fontSize: 12, color: "#2A2060", fontWeight: 700, textAlign: "center" }}>Fetching token…</div>
        </div>
      )}

      {token && !loading && (
        <div style={{ background: "rgba(0,255,157,0.06)", border: "1.5px solid rgba(0,255,157,0.18)", borderRadius: 12, padding: "12px 16px", marginTop: 12, animation: "popIn 0.35s ease", textAlign: "center" }}>
          <div style={{ fontSize: 13, color: "#00FF9D", fontWeight: 800 }}>✓ Fetched</div>
        </div>
      )}
    </div>
  );
}

/* ─── LANDING FOOTER ─────────────────────────────────────────────── */
function LandingFooter({ onConnect }: { onConnect: () => void }) {
  const [faqOpen, setFaqOpen] = useState<number | null>(null);

  return (
    <footer style={{ borderTop: "1px solid rgba(138,43,226,0.08)", background: "#03040A", position: "relative", zIndex: 1, overflow: "hidden" }}>
      <div style={{ position: "absolute", top: 0, left: "50%", transform: "translateX(-50%)", width: 800, height: 300, background: "radial-gradient(ellipse,rgba(138,43,226,0.04),transparent 70%)", pointerEvents: "none" }} />

      {/* Stats bar */}
      <div style={{ borderBottom: "1px solid rgba(138,43,226,0.07)", padding: "22px 24px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", flexWrap: "wrap" }}>
          {[
            { v: "4,100+", l: "Tokens Boosted", i: "🚀", c: "#b266ff" },
            { v: "$36M+", l: "Volume Forged", i: "📈", c: "#00F0FF" },
            { v: "<380ms", l: "Avg Confirmation", i: "⚡", c: "#00FF9D" },
            { v: "99.8%", l: "Tx Landing Rate", i: "🛡️", c: "#FF4500" },
            { v: "12", l: "Bot Subsystems", i: "⚙️", c: "#b266ff" },
            { v: "45+", l: "Routed Pools", i: "🌊", c: "#00F0FF" },
          ].map(function (s, i) {
            return (
              <div key={i} style={{ flex: "1 0 130px", padding: "14px 20px", borderRight: "1px solid rgba(138,43,226,0.06)", textAlign: "center" }}>
                <div style={{ fontSize: 18, marginBottom: 4 }}>{s.i}</div>
                <div className="exo" style={{ fontSize: 20, fontWeight: 900, color: s.c }}>{s.v}</div>
                <div style={{ fontSize: 10.5, color: "#5a4392", fontWeight: 700, marginTop: 2 }}>{s.l}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Main grid */}
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "48px 24px 40px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: 40 }} className="g2">

          {/* Brand */}
          <div>
            <Logo size={24} showSub />
            <p style={{ fontSize: 13, color: "#7A6A9B", lineHeight: 1.85, fontWeight: 500, marginTop: 16, marginBottom: 20, maxWidth: 280 }}>
              The high-performance neural volume engine for Solana tokens. Forge organic trading volume, climb trending lists, and accelerate liquidity flows safely.
            </p>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
              {[
                { label: "🐦 Twitter", color: "#1DA1F2" },
                { label: "✈️ Telegram", color: "#229ED9" },
                { label: "📖 Docs", color: "#8A2BE2" },
                { label: "🌐 bot.cortexlaunch.fun", color: "#00F0FF", href: "https://bot.cortexlaunch.fun" },
              ].map(function (item) {
                return (
                  <a key={item.label} href={item.href || "#"} onClick={item.href ? undefined : (e) => e.preventDefault()} target={item.href ? "_blank" : undefined} rel={item.href ? "noopener noreferrer" : undefined}
                    style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 12px", borderRadius: 8, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(138,43,226,0.1)", fontSize: 11.5, fontWeight: 700, color: item.color, textDecoration: "none", transition: "all 0.2s" }}>
                    {item.label}
                  </a>
                );
              })}
            </div>
            <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
              <span className="tag tg-live">● ENGINE ONLINE</span>
              <span className="tag tg-cyan">JUPITER ULTRA</span>
              <span className="tag tg-pur">JITO BUNDLES</span>
            </div>
          </div>

          {/* Features */}
          <div>
            <div className="exo" style={{ fontWeight: 900, fontSize: 12, color: "#C8D0F0", marginBottom: 14, textTransform: "uppercase", letterSpacing: 1.5 }}>Features</div>
            {[
              ["🌊", "Multi-Wallet Spray"],
              ["🛡️", "Jito Bundle Armor"],
              ["🔄", "Jupiter Ultra Swap"],
              ["⚡", "Helius RPC Turbo"],
              ["🎯", "Trending Ignition"],
              ["🔀", "Forge Randomizer™"],
              ["📈", "Buy Pressure Amp"],
              ["🔬", "Anti-Fingerprint"],
            ].map(function (pair) {
              return (
                <div key={pair[1]} style={{ display: "flex", gap: 7, alignItems: "center", padding: "5px 0", opacity: 0.85 }}>
                  <span style={{ fontSize: 12 }}>{pair[0]}</span>
                  <span style={{ fontSize: 12.5, color: "#7A6A9B", fontWeight: 600 }}>{pair[1]}</span>
                </div>
              );
            })}
          </div>

          {/* Pricing */}
          <div>
            <div className="exo" style={{ fontWeight: 900, fontSize: 12, color: "#C8D0F0", marginBottom: 14, textTransform: "uppercase", letterSpacing: 1.5 }}>Pricing</div>
            <div style={{ background: "rgba(138,43,226,0.06)", border: "1px solid rgba(138,43,226,0.16)", borderRadius: 14, padding: "14px 16px", marginBottom: 10 }}>
              <div style={{ fontSize: 10, color: "#7A6A9B", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>🔥 Basic Forge</div>
              <div className="exo" style={{ fontSize: 24, fontWeight: 900, color: "#b266ff" }}>0.7 SOL</div>
              <div style={{ fontSize: 11.5, color: "#5a4392", fontWeight: 600, lineHeight: 1.7, marginTop: 4 }}>20 wallets · 30 min · Optimized</div>
              <button className="btn-vol" style={{ marginTop: 10, padding: "8px 14px", fontSize: 12.5, borderRadius: 10, width: "100%" }} onClick={onConnect}>
                <span>Launch Basic →</span>
              </button>
            </div>
            <div style={{ background: "rgba(0,240,255,0.04)", border: "1px solid rgba(0,240,255,0.14)", borderRadius: 14, padding: "14px 16px" }}>
              <div style={{ fontSize: 10, color: "#1A5060", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>⚒️ Custom Forge</div>
              <div className="exo" style={{ fontSize: 20, fontWeight: 900, color: "#00F0FF" }}>0.5–50 SOL</div>
              <div style={{ fontSize: 11.5, color: "#1A3A4A", fontWeight: 600, lineHeight: 1.7, marginTop: 4 }}>Full control · 10–500 wallets</div>
              <button
                style={{ marginTop: 10, padding: "8px 14px", fontSize: 12.5, borderRadius: 10, width: "100%", background: "transparent", border: "1.5px solid rgba(0,240,255,0.25)", color: "#00F0FF", cursor: "pointer", fontFamily: "inherit", fontWeight: 700, transition: "all 0.2s" }}
                onClick={onConnect}>
                Configure →
              </button>
            </div>
          </div>

          {/* FAQ */}
          <div>
            <div className="exo" style={{ fontWeight: 900, fontSize: 12, color: "#C8D0F0", marginBottom: 14, textTransform: "uppercase", letterSpacing: 1.5 }}>FAQ</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
              {FAQS.map(function (f, i) {
                const isOpen = faqOpen === i;
                return (
                  <div key={i}
                    onClick={function () { setFaqOpen(isOpen ? null : i); }}
                    style={{ cursor: "pointer", borderRadius: 10, overflow: "hidden", border: "1px solid rgba(138,43,226," + (isOpen ? "0.2" : "0.07") + ")", transition: "all 0.2s" }}>
                    <div style={{ padding: "9px 12px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                      <span style={{ fontSize: 11.5, fontWeight: 700, color: isOpen ? "#b266ff" : "#7A6A9B", flex: 1, lineHeight: 1.4 }}>{f.q}</span>
                      <span style={{ color: "#5a4392", fontSize: 14, flexShrink: 0, transition: "transform 0.25s", transform: isOpen ? "rotate(45deg)" : "none" }}>+</span>
                    </div>
                    {isOpen && (
                      <div style={{ padding: "0 12px 10px", fontSize: 11.5, color: "#5a4392", lineHeight: 1.8, fontWeight: 500 }}>{f.a}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Tech strip */}
        <div style={{ marginTop: 44, paddingTop: 28, borderTop: "1px solid rgba(138,43,226,0.07)", display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 12 }}>
          {[
            { i: "⚡", t: "Helius Staked RPC", d: "Sub-380ms confirmations", c: "#FF4500" },
            { i: "🛡️", t: "Jito Bundle Network", d: "99.8% MEV bundle landing", c: "#8A2BE2" },
            { i: "🔄", t: "Jupiter Ultra Swap", d: "Best route aggregation swap", c: "#00F0FF" },
            { i: "◎", t: "Solana Mainnet", d: "All activities fully verified", c: "#b266ff" },
          ].map(function (item, i) {
            return (
              <div key={i} style={{ display: "flex", gap: 10, alignItems: "center", padding: "11px 14px", background: "rgba(255,255,255,0.015)", border: "1px solid rgba(138,43,226,0.07)", borderRadius: 11 }}>
                <div style={{ width: 34, height: 34, borderRadius: 9, background: item.c + "14", border: "1px solid " + item.c + "28", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>{item.i}</div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 800, color: "#C8D0F0" }}>{item.t}</div>
                  <div style={{ fontSize: 10.5, color: "#2A2060", fontWeight: 600 }}>{item.d}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom bar */}
        <div style={{ borderTop: "1px solid rgba(138,43,226,0.06)", padding: "16px 24px", marginTop: 32 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
            <div style={{ fontSize: 11, color: "#1E1A50", fontWeight: 700 }}>© 2026 BOTCORTEX · Powered by Solana ◎</div>
            <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
              {["Terms", "Privacy", "Docs", "Twitter", "Telegram"].map(function (l) {
                return (
                  <a key={l} href="#" onClick={(e) => e.preventDefault()} style={{ fontSize: 11, fontWeight: 700, color: "#1E1A50", textDecoration: "none" }}>{l}</a>
                );
              })}
            </div>
            <div style={{ display: "flex", gap: 7 }}>
              <span className="tag" style={{ background: "rgba(0,255,157,0.08)", color: "#00DD88", border: "1px solid rgba(0,255,157,0.18)", fontSize: 9 }}>✓ ACTIVE</span>
              <span className="tag" style={{ background: "rgba(138,43,226,0.08)", color: "#8A2BE2", border: "1px solid rgba(138,43,226,0.2)", fontSize: 9 }}>NON-CUSTODIAL</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ─── BOT CONFIGURATOR ──────────────────────────────────────────── */
interface BotAppProps {
  onLive: (c: typeof DEFAULT_CFG & { jobId: string }, t: TokenData, asModal?: boolean) => void;
}

function BotApp({ onLive }: BotAppProps) {
  const { publicKey, connected, sendTransaction } = useWallet();
  const { connection } = useConnection();
  const { network, isDevnet } = useNetwork();
  const { setVisible } = useWalletModal();
  const [plan, setPlan] = useState<"basic" | "custom">("basic");
  const [mint, setMint] = useState("");
  const [token, setToken] = useState<TokenData | null>(null);
  const [fetching, setFetch] = useState(false);
  const [fetchErr, setErr] = useState("");
  const [cfg, setCfg] = useState(Object.assign({}, DEFAULT_CFG));
  const [launching, setLaunch] = useState(false);

  function upCfg(k: string, v: any) {
    setCfg(function (c) { return Object.assign({}, c, { [k]: v }); });
  }

  const handleFetch = useCallback(async function () {
    if (!mint.trim()) { setErr("Please enter a token mint address."); return; }
    setErr(""); setFetch(true); setToken(null);
    try {
      const data = await apiService.fetchTokenData(mint.trim());
      if (data) {
        setToken(data);
      } else {
        setErr("Token not found. Check the mint address.");
      }
    } catch (_) {
      setErr("Failed to resolve token data.");
    } finally {
      setFetch(false);
    }
  }, [mint]);

  function handlePlan(p: "basic" | "custom") {
    setPlan(p);
    if (p === "basic") setCfg(Object.assign({}, DEFAULT_CFG));
  }

  const est = calcEst(cfg);

  async function handleLaunch() {
    if (!token) { setErr("Fetch your token first."); return; }
    if (!publicKey || !connected) { setErr("Connect your wallet first."); return; }
    setLaunch(true);
    setErr("");
    try {
      const { packageType, paymentSol } = getPackagePayment(plan, cfg);
      const botConfig = toBotConfig(plan === "basic" ? DEFAULT_CFG : cfg);
      const workerInfo = await apiService.getWorkerInfo(packageType);

      const lamports = Math.floor(paymentSol * LAMPORTS_PER_SOL);
      const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash();
      const paymentTx = new Transaction().add(
        SystemProgram.transfer({
          fromPubkey: publicKey,
          toPubkey: new PublicKey(workerInfo.workerPublicKey),
          lamports,
        })
      );
      paymentTx.recentBlockhash = blockhash;
      paymentTx.feePayer = publicKey;

      const paymentSignature = await sendTransaction(paymentTx, connection);
      await connection.confirmTransaction(
        { signature: paymentSignature, blockhash, lastValidBlockHeight },
        "confirmed"
      );

      const res = await apiService.startBot({
        mintAddress: mint.trim(),
        packageType,
        targetVolume: plan === "custom" ? est.volSOL * 145 : undefined,
        userPaidAmount: paymentSol,
        userWallet: publicKey.toBase58(),
        paymentSignature,
        botConfig,
        network,
      });

      if (res && res.jobId) {
        const updatedConfig = Object.assign({}, plan === "basic" ? DEFAULT_CFG : cfg, { jobId: res.jobId });
        onLive(updatedConfig, token, isDevnet);
      }
    } catch (e: any) {
      setErr(e.message || "Failed to initialize volume sequence.");
    } finally {
      setLaunch(false);
    }
  }

  return (
    <div style={{ minHeight: "100vh", background: "#05070F", position: "relative" }}>
      <BgScene />

      {/* Topbar */}
      <div style={{ height: 58, background: "rgba(8,11,24,0.97)", borderBottom: "1px solid rgba(138,43,226,0.08)", display: "flex", alignItems: "center", padding: "0 22px", position: "sticky", top: 0, zIndex: 50, backdropFilter: "blur(16px)" }}>
        <div style={{ flex: 1, display: "flex", alignItems: "center", minWidth: 0 }}>
          <Logo showSub />
        </div>
        <SystemStateBadge />
        <div style={{ flex: 1, display: "flex", gap: 8, alignItems: "center", justifyContent: "flex-end", minWidth: 0 }}>
          <NetworkStatusDot />
          <WalletMenuButton />
        </div>
      </div>

      <MarqueeBanner />

      {/* Dashboard (blurred if no wallet) */}
      <div style={{ position: "relative", minHeight: "calc(100vh - 58px)" }}>
        <div style={{ filter: connected ? "none" : "blur(5px)", opacity: connected ? 1 : 0.4, pointerEvents: connected ? "all" : "none", transition: "all 0.4s ease" }}>

          <div style={{ maxWidth: 1040, margin: "0 auto", padding: "28px 20px 60px", position: "relative", zIndex: 1 }}>
            
            {/* Rebranded typography header block */}
            <div style={{ marginBottom: 36, textAlign: "center" }}>
              <div style={{ display: "flex", justifyContent: "center", gap: 24, fontSize: "clamp(24px, 5vw, 42px)", fontWeight: 900, marginBottom: 8 }} className="exo">
                <span className="launch-text">LAUNCH.</span>
                <span className="nuke-text">NUKE.</span>
                <span className="earn-text">EARN.</span>
              </div>
              <p style={{ color: "#7A6A9B", fontSize: 14, fontWeight: 500 }}>
                Input token mint · Set custom engine variables · Deploy volume algorithms instantly.
              </p>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 380px", gap: 18, alignItems: "start" }} className="g2">

              {/* Left column */}
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

                {/* Step 1: Token */}
                <div className="card" style={{ padding: "24px" }}>
                  <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 16 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 9, background: "linear-gradient(135deg,#8A2BE2,#5c18a0)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, fontSize: 13, color: "#fff" }}>1</div>
                    <div className="exo" style={{ fontWeight: 900, fontSize: 16, color: "#C8D0F0" }}>Select Token</div>
                    <span className="tag tg-cyan" style={{ marginLeft: "auto" }}>Helius Staked RPC</span>
                  </div>
                  <TokenWidget token={token} loading={fetching} error={fetchErr} mint={mint}
                    onMint={function (m) { setMint(m); setErr(""); }}
                    onFetch={handleFetch}
                  />
                </div>

                {/* Step 2: Plan */}
                <div className="card" style={{ padding: "24px" }}>
                  <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 18 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 9, background: "linear-gradient(135deg,#8A2BE2,#5c18a0)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, fontSize: 13, color: "#fff" }}>2</div>
                    <div className="exo" style={{ fontWeight: 900, fontSize: 16, color: "#C8D0F0" }}>Choose Plan</div>
                  </div>

                  <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
                    <button className={"seg" + (plan === "basic" ? " on" : "")} style={{ flex: 1, padding: "11px", textAlign: "center", borderRadius: 12 }} onClick={function () { handlePlan("basic"); }}>
                      🔥 Basic — <strong>0.7 SOL</strong>
                    </button>
                    <button className={"seg-c" + (plan === "custom" ? " on" : "")} style={{ flex: 1, padding: "11px", textAlign: "center", borderRadius: 12 }} onClick={function () { handlePlan("custom"); }}>
                      ⚒️ Custom — <strong>0.5–50 SOL</strong>
                    </button>
                  </div>

                  {plan === "basic" ? (
                    <div style={{ background: "rgba(138,43,226,0.04)", border: "1px solid rgba(138,43,226,0.12)", borderRadius: 13, padding: "15px 17px" }}>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 9 }}>
                        {[
                          ["⏱️ Duration", "30 minutes"], ["👛 Wallets", "20 wallets"],
                          ["🔄 Frequency", "~3 trades/min"], ["📈 Buy Pressure", "75% buys"],
                          ["🎯 Trending", "ON – Ignition"], ["🛡️ Jito Tip", "Med (0.0003)"],
                          ["🔀 Stealth", "Full Anti-FP"], ["💰 Budget", "0.70 SOL"],
                        ].map(function (pair) {
                          return (
                            <div key={pair[0]} style={{ background: "rgba(255,255,255,0.015)", borderRadius: 8, padding: "7px 11px" }}>
                              <div style={{ fontSize: 9.5, color: "#5a4392", fontWeight: 700 }}>{pair[0]}</div>
                              <div style={{ fontSize: 12, color: "#C8D0F0", fontWeight: 800, marginTop: 2 }}>{pair[1]}</div>
                            </div>
                          );
                        })}
                      </div>
                      <div style={{ marginTop: 10, fontSize: 11.5, color: "#5a4392", fontWeight: 700, textAlign: "center" }}>✅ Pre-optimized · Auto-adjust systems active</div>
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

                      {/* SOL Budget */}
                      <div style={{ background: "rgba(0,240,255,0.03)", border: "1px solid rgba(0,240,255,0.1)", borderRadius: 14, padding: "17px 19px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                          <div className="exo" style={{ fontWeight: 800, fontSize: 13.5, color: "#C8D0F0" }}>💰 Total SOL Budget</div>
                          <div className="exo" style={{ fontSize: 28, fontWeight: 900, color: "#00F0FF" }}>
                            {cfg.solBudget.toFixed(1)} <span style={{ fontSize: 13, color: "#1A4A5A" }}>SOL</span>
                          </div>
                        </div>
                        <input type="range" className="cyan-range" min="0.5" max="50" step="0.1" value={cfg.solBudget}
                          onChange={function (e) {
                            const v = parseFloat(e.target.value);
                            upCfg("solBudget", v);
                            upCfg("minTrade", Math.max(0.005, v * 0.008));
                            upCfg("maxTrade", Math.max(0.02, v * 0.025));
                          }}
                          style={{ height: 6, marginBottom: 8 }}
                        />
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10.5, color: "#1A3A3A", fontWeight: 700 }}>
                          <span>0.5 SOL</span>
                          <div style={{ display: "flex", gap: 5 }}>
                            {[1, 5, 10, 25, 50].map(function (v) {
                              return (
                                <button key={v} onClick={function () { upCfg("solBudget", v); }}
                                  style={{ padding: "2px 7px", borderRadius: 5, cursor: "pointer", fontSize: 10.5, fontWeight: 800, background: cfg.solBudget === v ? "rgba(0,240,255,0.12)" : "transparent", border: "1px solid " + (cfg.solBudget === v ? "rgba(0,240,255,0.35)" : "rgba(0,240,255,0.1)"), color: cfg.solBudget === v ? "#00F0FF" : "#1A3A4A", fontFamily: "inherit" }}>
                                  {v}
                                </button>
                              );
                            })}
                          </div>
                          <span>50 SOL</span>
                        </div>
                      </div>

                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 13 }} className="g2">

                        {/* Duration */}
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 800, color: "#4A3878", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.6 }}>⏱️ Duration</div>
                          <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                            {[15, 30, 60, 120, 240].map(function (m) {
                              return (
                                <button key={m} className={"seg" + (cfg.duration === m ? " on" : "")} style={{ padding: "7px 10px", fontSize: 11.5 }} onClick={function () { upCfg("duration", m); }}>
                                  {m >= 60 ? (m / 60) + "h" : m + "m"}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Wallets */}
                        <div>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 7 }}>
                            <div style={{ fontSize: 11, fontWeight: 800, color: "#4A3878", textTransform: "uppercase", letterSpacing: 0.6 }}>👛 Wallets</div>
                            <span className="exo" style={{ fontSize: 15, fontWeight: 900, color: "#b266ff" }}>{cfg.walletCount}</span>
                          </div>
                          <input type="range" min="10" max="500" step="5" value={cfg.walletCount}
                            onChange={function (e) { upCfg("walletCount", parseInt(e.target.value)); }}
                            style={{ height: 5, width: "100%" }}
                          />
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#2A2060", fontWeight: 700, marginTop: 3 }}>
                            <span>10</span><span>500</span>
                          </div>
                        </div>

                        {/* Frequency */}
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 800, color: "#4A3878", marginBottom: 7, textTransform: "uppercase", letterSpacing: 0.6 }}>🔄 Frequency</div>
                          <div style={{ display: "flex", gap: 4 }}>
                            {["slow", "normal", "fast", "blast"].map(function (f) {
                              return (
                                <button key={f} className={"seg" + (cfg.freqPreset === f ? " on" : "")} style={{ padding: "6px 8px", fontSize: 10.5, flex: 1 }} onClick={function () { upCfg("freqPreset", f); }}>
                                  {f.charAt(0).toUpperCase() + f.slice(1)}
                                </button>
                              );
                            })}
                          </div>
                          <div style={{ fontSize: 10.5, color: "#2A2060", marginTop: 4, fontWeight: 700 }}>{FREQ_LABELS[cfg.freqPreset]}</div>
                        </div>

                        {/* Jito */}
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 800, color: "#4A3878", marginBottom: 7, textTransform: "uppercase", letterSpacing: 0.6 }}>🛡️ Jito Tip</div>
                          <div style={{ display: "flex", gap: 4 }}>
                            {["low", "med", "high", "ultra"].map(function (j) {
                              return (
                                <button key={j} className={"seg" + (cfg.jitoTip === j ? " on" : "")} style={{ padding: "6px 7px", fontSize: 10.5, flex: 1, textTransform: "capitalize" }} onClick={function () { upCfg("jitoTip", j); }}>
                                  {j}
                                </button>
                              );
                            })}
                          </div>
                          <div style={{ fontSize: 10.5, color: "#2A2060", marginTop: 4, fontWeight: 700 }}>{JITO_LABELS[cfg.jitoTip]}</div>
                        </div>

                      </div>

                      {/* Buy Pressure */}
                      <div>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 7, alignItems: "center" }}>
                          <div style={{ fontSize: 11, fontWeight: 800, color: "#4A3878", textTransform: "uppercase", letterSpacing: 0.6 }}>📈 Buy Pressure</div>
                          <div className="exo" style={{ fontSize: 14, fontWeight: 900 }}>
                            <span style={{ color: "#00FF9D" }}>{cfg.buyPressure}% BUY</span>
                            <span style={{ color: "#2A2060", margin: "0 4px" }}>/</span>
                            <span style={{ color: "#FF5577" }}>{100 - cfg.buyPressure}% SELL</span>
                          </div>
                        </div>
                        <input type="range" min="50" max="95" step="5" value={cfg.buyPressure}
                          onChange={function (e) { upCfg("buyPressure", parseInt(e.target.value)); }}
                          style={{ height: 5, width: "100%" }}
                        />
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#2A2060", fontWeight: 700, marginTop: 3 }}>
                          <span>50/50 Neutral</span><span>95/5 Max Bullish</span>
                        </div>
                      </div>

                      {/* Toggles */}
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 9 }}>
                        {[
                          { k: "trendingMode", e: "🎯", l: "Trending Ignition", d: "Front-load 40% vol first 8 min" },
                          { k: "antiBot", e: "🔀", l: "Anti-Fingerprint", d: "Randomize timing & wallet sizes" },
                          { k: "splitOrders", e: "🌊", l: "Split Order Routing", d: "Multi-pool Jupiter execution" },
                        ].map(function (item) {
                          const val = (cfg as any)[item.k];
                          return (
                            <div key={item.k}
                              onClick={function () { upCfg(item.k, !val); }}
                              style={{ background: "rgba(138,43,226,0.02)", border: "1px solid rgba(138,43,226," + (val ? "0.18" : "0.06") + ")", borderRadius: 11, padding: "11px 13px", display: "flex", alignItems: "center", gap: 9, cursor: "pointer", transition: "all 0.2s" }}>
                              <span style={{ fontSize: 17 }}>{item.e}</span>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 12, fontWeight: 800, color: "#C8D0F0" }}>{item.l}</div>
                                <div style={{ fontSize: 10, color: "#2A2060", fontWeight: 600 }}>{item.d}</div>
                              </div>
                              <div className={"toggle " + (val ? "on" : "off")} />
                            </div>
                          );
                        })}
                      </div>

                    </div>
                  )}
                </div>

              </div>

              {/* Right column */}
              <div style={{ display: "flex", flexDirection: "column", gap: 14, position: "sticky", top: 76 }}>

                {/* Estimates */}
                <div className="card card-glow" style={{ padding: "22px" }}>
                  <div className="exo" style={{ fontWeight: 900, fontSize: 15, color: "#C8D0F0", marginBottom: 16 }}>📊 Forge Estimates</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {[
                      ["Total Trades", "~" + Math.round(est.trades * EST_SHOW_MULT).toLocaleString(), "#b266ff"],
                      ["Buy Orders", "~" + Math.round(est.buys * EST_SHOW_MULT).toLocaleString(), "#00FF9D"],
                      ["Sell Orders", "~" + Math.round(est.sells * EST_SHOW_MULT).toLocaleString(), "#FF5577"],
                      ["Volume (SOL)", "~" + (est.volSOL * EST_SHOW_MULT).toFixed(2), "#00F0FF"],
                      ["Wallets Used", "" + Math.round(cfg.walletCount * EST_SHOW_MULT), "#b266ff"],
                      ["Duration", cfg.duration < 60 ? cfg.duration + "m" : (cfg.duration / 60) + "h", "#FF4500"],
                      ["Trades/Wallet", "~" + est.tpw, "#8A2BE2"],
                    ].map(function (row) {
                      return (
                        <div key={row[0]} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid rgba(138,43,226,0.06)" }}>
                          <span style={{ fontSize: 12.5, color: "#5a4392", fontWeight: 700 }}>{row[0]}</span>
                          <span className="exo" style={{ fontSize: 13, color: row[2], fontWeight: 900 }}>{row[1]}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ marginTop: 12, background: "rgba(0,255,157,0.06)", border: "1px solid rgba(0,255,157,0.14)", borderRadius: 9, padding: "9px 12px", fontSize: 11.5, color: "#2A8A50", fontWeight: 700, textAlign: "center" }}>
                    🎯 Trending Ignition: Front-loads 40% vol in first 8 min
                  </div>
                </div>

                {/* Token preview */}
                {token && (
                  <div style={{ background: "rgba(0,255,157,0.06)", border: "1.5px solid rgba(0,255,157,0.18)", borderRadius: 12, padding: "12px 16px", animation: "popIn 0.3s ease", textAlign: "center" }}>
                    <div style={{ fontSize: 13, color: "#00FF9D", fontWeight: 800 }}>✓ Fetched</div>
                  </div>
                )}

                {/* Cost */}
                <div style={{ background: "rgba(138,43,226,0.07)", border: "1px solid rgba(138,43,226,0.18)", borderRadius: 14, padding: "14px 16px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontSize: 10.5, color: "#5a4392", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>Total Cost</div>
                      <div className="exo" style={{ fontSize: 26, fontWeight: 900, color: "#b266ff" }}>
                        {plan === "basic" ? "0.7" : cfg.solBudget.toFixed(1)} SOL
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 10.5, color: "#2A2060", fontWeight: 700 }}>Platform Fee</div>
                      <div className="exo" style={{ fontSize: 14, fontWeight: 800, color: "#5a4392" }}>Included ✅</div>
                    </div>
                  </div>
                </div>

                {/* Launch button */}
                <button className="btn-buy"
                  style={{ padding: "16px", fontSize: 16, borderRadius: 16, width: "100%", opacity: !token ? 0.4 : 1, cursor: !token ? "not-allowed" : "pointer", animation: token ? "heartbeat 2s ease-in-out infinite" : "none" }}
                  onClick={handleLaunch}
                  disabled={!token || launching}>
                  {launching ? "⏳ Initializing Engine…" : !token ? "⚠️ Fetch Token First" : "🚀 Launch Volume Bot"}
                </button>

                {!token && (
                  <div style={{ fontSize: 11.5, color: "#2A2060", textAlign: "center", fontWeight: 600 }}>↑ Paste your token mint address above to continue</div>
                )}

                <div style={{ fontSize: 11, color: "#1E1A50", textAlign: "center", fontWeight: 600, lineHeight: 1.7 }}>
                  🔑 Non-custodial · Sign each transaction · Cancel anytime
                  <br />
                  ⛓️ Transactions broadcasted in real-time
                </div>

              </div>
            </div>
          </div>

        </div>

        {/* Lock overlay when no wallet */}
        {!connected && <WalletLock onConnect={function () { setVisible(true); }} />}
      </div>

      <LandingFooter onConnect={function () { setVisible(true); }} />
    </div>
  );
}

/* ─── LIVE DASHBOARD ─────────────────────────────────────────────── */
interface LiveDashProps {
  cfg: typeof DEFAULT_CFG & { jobId: string };
  token: TokenData;
  onStop: () => void;
  minimized?: boolean;
  onMinimize?: () => void;
  onExpand?: () => void;
  overlay?: boolean;
}

interface MinimizedLivePanelProps {
  token: TokenData;
  stats: ReturnType<typeof useLiveEngine>["stats"];
  onExpand: () => void;
  onStop: () => void;
}

function MinimizedLivePanel({ token, stats, onExpand, onStop }: MinimizedLivePanelProps) {
  const drag = useDraggablePanel({
    x: typeof window !== "undefined" ? Math.max(12, window.innerWidth - Math.min(320, window.innerWidth - 24) - 12) : 16,
    y: typeof window !== "undefined" ? Math.max(16, window.innerHeight - 88) : 16,
  }, 320);

  return (
    <div
      style={{
        position: "fixed",
        left: drag.pos.x,
        top: drag.pos.y,
        zIndex: 500,
        width: 320,
        borderRadius: 14,
        border: "1.5px solid rgba(138,43,226,0.35)",
        background: "rgba(8,11,24,0.96)",
        boxShadow: "0 12px 40px rgba(0,0,0,0.55)",
        overflow: "hidden",
        touchAction: "none",
      }}
    >
      <div
        onPointerDown={drag.onPointerDown}
        onPointerMove={drag.onPointerMove}
        onPointerUp={drag.onPointerUp}
        style={{
          padding: "10px 12px",
          borderBottom: "1px solid rgba(138,43,226,0.12)",
          cursor: "grab",
          display: "flex",
          alignItems: "center",
          gap: 8,
          userSelect: "none",
        }}
      >
        <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#00FF9D", boxShadow: "0 0 8px #00FF9D", flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="exo" style={{ fontSize: 11, fontWeight: 900, color: "#b266ff" }}>ENGINE RUNNING</div>
          <div className="mono" style={{ fontSize: 10, color: "#5a4392", marginTop: 2 }}>
            {token.logo} ${token.symbol} · {fmtTime(stats.elapsed)} · {stats.trades} tx · {fmtUsd(stats.totalVolume)}
          </div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 6, padding: "8px 10px" }}>
        <button type="button" className="btn-ghost" style={{ flex: 1, padding: "6px 8px", fontSize: 11 }} onClick={onExpand}>
          Expand
        </button>
        <button
          type="button"
          style={{ padding: "6px 10px", fontSize: 11, background: "rgba(255,45,85,0.08)", border: "1.5px solid rgba(255,45,85,0.22)", borderRadius: 10, cursor: "pointer", color: "#FF5577", fontFamily: "inherit", fontWeight: 700 }}
          onClick={onStop}
        >
          Stop
        </button>
      </div>
    </div>
  );
}

function LiveDash({ cfg, token, onStop, minimized, onMinimize, onExpand, overlay }: LiveDashProps) {
  const { price, activity, stats, basePrice } = useLiveEngine(cfg, token);

  const totalTimeSec = cfg.duration * 60;
  const pct = Math.min(100, (stats.elapsed / totalTimeSec) * 100);
  const priceChg = basePrice > 0 ? ((price - basePrice) / basePrice) * 100 : 0;

  async function handleStop() {
    try {
      await apiService.stopBot(cfg.jobId);
    } catch (err) {
      console.error("Failed to stop bot:", err);
    }
    onStop();
  }

  if (minimized && onExpand) {
    return (
      <MinimizedLivePanel
        token={token}
        stats={stats}
        onExpand={onExpand}
        onStop={handleStop}
      />
    );
  }

  const dash = (
    <div style={{ minHeight: overlay ? "auto" : "100vh", background: "#05070F", position: "relative" }}>
      <BgScene />

      <div style={{ height: 56, background: "rgba(8,11,24,0.97)", borderBottom: "1px solid rgba(138,43,226,0.1)", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 22px", position: "sticky", top: 0, zIndex: 50, backdropFilter: "blur(16px)" }}>
        <Logo showSub />
        <div style={{ display: "flex", gap: 9, alignItems: "center" }}>
          <span className="tag tg-live">
            <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: "#00FF9D", animation: "ping 1.4s ease-in-out infinite" }} />
            ENGINE RUNNING
          </span>
          <div style={{ fontSize: 12, color: "#C8D0F0", fontWeight: 800 }}>{token.logo || '🪙'} ${token.symbol}</div>
          {onMinimize && (
            <button type="button" className="btn-ghost" onClick={onMinimize} title="Minimize" style={{ padding: "5px 10px", fontSize: 14, lineHeight: 1 }}>
              —
            </button>
          )}
          <button
            style={{ background: "rgba(255,45,85,0.08)", border: "1.5px solid rgba(255,45,85,0.22)", borderRadius: 9, padding: "5px 13px", cursor: "pointer", color: "#FF5577", fontFamily: "inherit", fontWeight: 700, fontSize: 11.5 }}
            onClick={handleStop}
          >
            ⏹️ Stop Bot
          </button>
        </div>
      </div>

      <MarqueeBanner />

      <div style={{ maxWidth: 1160, margin: "0 auto", padding: "20px 20px 60px", position: "relative", zIndex: 1 }}>

        <div style={{ background: "#080B18", border: "1px solid rgba(138,43,226,0.09)", borderRadius: 12, padding: "13px 17px", marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 8 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <span className="tag tg-live">● FORGING VOLUME</span>
              <span className="exo" style={{ fontSize: 13, color: "#C8D0F0", fontWeight: 800 }}>{fmtTime(stats.elapsed)} / {fmtTime(cfg.duration * 60)}</span>
            </div>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              {stats.trending && <span className="tag tg-green">🟢 ALGORITHMIC IMPACT DETECTED</span>}
              <span style={{ fontSize: 11.5, color: "#2A2060", fontWeight: 700 }}>Latency ⚡ {stats.avgConfirm}ms avg</span>
              <span style={{ fontSize: 11.5, color: "#2A2060", fontWeight: 700 }}>Jito 🛡️ {JITO_LABELS[cfg.jitoTip]}</span>
            </div>
          </div>
          <div style={{ height: 7, background: "rgba(138,43,226,0.06)", borderRadius: 999, overflow: "hidden" }}>
            <div style={{ height: "100%", background: "linear-gradient(90deg,#8A2BE2,#FF4500,#00F0FF)", borderRadius: 999, transition: "width 1s ease", width: pct + "%", boxShadow: "0 0 8px rgba(138,43,226,0.5)" }} />
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(148px,1fr))", gap: 11, marginBottom: 16 }}>
          {[
            { e: "💹", l: "Volume USD", v: fmtUsd(stats.totalVolume), c: "#00F0FF", s: "total" },
            { e: "🔄", l: "Total Trades", v: stats.trades.toLocaleString(), c: "#b266ff", s: "executed" },
            { e: "🟢", l: "Buy Orders", v: stats.buys.toLocaleString(), c: "#00FF9D", s: stats.trades > 0 ? Math.round(stats.buys / stats.trades * 100) + "%" : cfg.buyPressure + "%" },
            { e: "🔴", l: "Sell Orders", v: stats.sells.toLocaleString(), c: "#FF5577", s: stats.trades > 0 ? Math.round(stats.sells / stats.trades * 100) + "%" : (100 - cfg.buyPressure) + "%" },
            { e: "👛", l: "Active Wallets", v: Math.max(stats.wallets, 1).toLocaleString(), c: "#b266ff", s: "of " + cfg.walletCount },
            { e: "⚡", l: "Avg Confirm", v: stats.avgConfirm + "ms", c: "#FF4500", s: "Helius RPC" },
            { e: "◎", l: "SOL Fees", v: stats.solSpent.toFixed(3), c: "#8A2BE2", s: "SOL" },
            { e: "📈", l: "Price Move", v: fmtPct(priceChg), c: priceChg >= 0 ? "#00FF9D" : "#FF5577", s: fmtPrice(price) },
          ].map(function (s) {
            return (
              <div key={s.l} className="card" style={{ padding: "13px 15px" }}>
                <div style={{ fontSize: 19, marginBottom: 4 }}>{s.e}</div>
                <div className="exo" style={{ fontSize: 17, fontWeight: 900, color: s.c, animation: "slideUp 0.4s ease" }}>{s.v}</div>
                <div style={{ fontSize: 9.5, color: "#2A2060", fontWeight: 700, marginTop: 2 }}>{s.s}</div>
                <div style={{ fontSize: 9.5, color: "#1A1A40", fontWeight: 700, marginTop: 1 }}>{s.l}</div>
              </div>
            );
          })}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 14, alignItems: "start" }} className="g2">

          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
            <div style={{ padding: "13px 17px", borderBottom: "1px solid rgba(138,43,226,0.07)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div className="exo" style={{ fontWeight: 900, fontSize: 13.5, color: "#C8D0F0" }}>⚡ Live Transaction Feed</div>
              <span className="tag tg-live">● LIVE</span>
            </div>
            <div style={{ height: 460, overflowY: "auto" }}>
              {activity.length === 0 ? (
                <div style={{ padding: "40px", textAlign: "center", color: "#2A2060", fontSize: 13, fontWeight: 700 }}>
                  Initializing block routing connections…
                </div>
              ) : activity.map(function (row, i) {
                if (row.kind === "system") {
                  return (
                    <div key={row.id + i} style={{ padding: "9px 17px", borderBottom: "1px solid rgba(138,43,226,0.03)", display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontSize: 12 }}>⚡</span>
                      <span className="mono" style={{ fontSize: 11, color: "#5a4392" }}>{row.message}</span>
                      <span style={{ marginLeft: "auto", fontSize: 9, color: "#2A2060", fontWeight: 700 }}>SYNC</span>
                    </div>
                  );
                }
                return (
                  <div key={row.id + i} className="live-row" style={{ padding: "9px 17px", borderBottom: "1px solid rgba(138,43,226,0.03)", display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 26, height: 26, borderRadius: 7, background: row.dir === "buy" ? "rgba(0,255,157,0.1)" : "rgba(255,45,85,0.09)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, flexShrink: 0 }}>
                      {row.dir === "buy" ? "🟢" : "🔴"}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <span style={{ fontSize: 11.5, fontWeight: 900, color: row.dir === "buy" ? "#00FF9D" : "#FF5577", textTransform: "uppercase" }}>{row.dir}</span>
                        <span className="exo" style={{ fontSize: 12.5, fontWeight: 900, color: "#C8D0F0" }}>{row.amount!.toFixed(4)} SOL</span>
                        <span style={{ fontSize: 11, color: "#2A2060", fontWeight: 600 }}>≈ {fmtUsd(row.usdValue!)}</span>
                      </div>
                      <div style={{ display: "flex", gap: 7, marginTop: 2 }}>
                        <span className="mono" style={{ fontSize: 9.5, color: "#1A1A40" }}>{fmtAddr(row.wallet!)}</span>
                        <span className="mono" style={{ fontSize: 9.5, color: "#1A1A40" }}>sig {row.hash!.slice(0, 8)}...</span>
                      </div>
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <div style={{ fontSize: 10.5, fontWeight: 800, color: "#FF4500" }}>{row.ms}ms</div>
                      <div style={{ fontSize: 9, color: "#1A1A40", fontWeight: 700 }}>Jito ⚡</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
            <div className="card" style={{ padding: "17px", borderColor: "rgba(0,240,255,0.12)" }}>
              <div className="exo" style={{ fontWeight: 900, fontSize: 13, color: "#C8D0F0", marginBottom: 11 }}>🪙 Token Dashboard</div>
              <div style={{ display: "flex", gap: 11, alignItems: "center", marginBottom: 11 }}>
                <div style={{ fontSize: 32 }}>{token.logo || '🪙'}</div>
                <div>
                  <div className="exo" style={{ fontWeight: 900, fontSize: 16, color: "#C8D0F0" }}>${token.symbol}</div>
                  <div style={{ fontSize: 13, fontWeight: 900, color: priceChg >= 0 ? "#00FF9D" : "#FF5577" }}>
                    {fmtPrice(price)} <span style={{ marginLeft: 4, fontSize: 11 }}>{fmtPct(priceChg)}</span>
                  </div>
                </div>
              </div>
              <div style={{ height: 1, background: "linear-gradient(90deg,transparent,rgba(0,240,255,0.14),transparent)", margin: "11px 0" }} />
              {[
                ["Market Cap", fmtMcap((token.mcap ?? basePrice * 1e9) * (price / basePrice)), "#00F0FF"],
                ["Session Volume", fmtUsd(stats.totalVolume), "#00FF9D"],
                ["Session Uptime", fmtTime(stats.elapsed), "#b266ff"],
              ].map(function (row) {
                return (
                  <div key={row[0]} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0" }}>
                    <span style={{ fontSize: 11, color: "#2A2060", fontWeight: 700 }}>{row[0]}</span>
                    <span className="exo" style={{ fontSize: 12.5, color: row[2], fontWeight: 900 }}>{row[1]}</span>
                  </div>
                );
              })}
            </div>

            <div style={{ background: stats.trending ? "rgba(0,255,157,0.05)" : "rgba(138,43,226,0.03)", border: "1.5px solid rgba(0,255,157," + (stats.trending ? "0.28" : "0.08") + ")", borderRadius: 15, padding: "15px 17px", transition: "all 0.6s" }}>
              <div className="exo" style={{ fontWeight: 900, fontSize: 12.5, color: stats.trending ? "#00FF9D" : "#C8D0F0", marginBottom: 10 }}>
                {stats.trending ? "🟢 ALGORITHMIC IMPACT CONFIRMED" : "🎯 Algorithmic Impact Status"}
              </div>
              {[
                { l: "Dexscreener", pct: stats.trending ? 100 : Math.min(100, (stats.totalVolume / 1200) * 100), c: "#00FF9D" },
                { l: "Birdeye", pct: stats.trending ? 100 : Math.min(100, (stats.totalVolume / 800) * 100), c: "#b266ff" },
                { l: "Axiom", pct: stats.trending ? 100 : Math.min(100, (stats.totalVolume / 1000) * 100), c: "#00F0FF" },
              ].map(function (row) {
                return (
                  <div key={row.l} style={{ marginBottom: 9 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ fontSize: 11, color: "#4A3878", fontWeight: 700 }}>{row.l}</span>
                      <span style={{ fontSize: 11, color: row.c, fontWeight: 800 }}>{Math.round(row.pct)}%</span>
                    </div>
                    <div style={{ height: 5, background: "rgba(138,43,226,0.05)", borderRadius: 999, overflow: "hidden" }}>
                      <div style={{ height: "100%", background: row.c, borderRadius: 999, width: row.pct + "%", transition: "width 0.8s ease" }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (overlay) {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 200,
          background: "rgba(5,7,15,0.88)",
          backdropFilter: "blur(6px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 16,
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: 1080,
            maxHeight: "92vh",
            overflow: "auto",
            borderRadius: 18,
            border: "1.5px solid rgba(138,43,226,0.28)",
            boxShadow: "0 24px 80px rgba(0,0,0,0.6)",
            background: "#05070F",
          }}
        >
          {dash}
        </div>
      </div>
    );
  }

  return dash;
}

/* ─── ROOT ───────────────────────────────────────────────────────── */
export default function BotCortexDashboard() {
  const [view, setView] = useState("app");
  const [cfg, setCfg] = useState<(typeof DEFAULT_CFG & { jobId: string }) | null>(null);
  const [token, setToken] = useState<TokenData | null>(null);
  const [modalLive, setModalLive] = useState<{ cfg: typeof DEFAULT_CFG & { jobId: string }; token: TokenData } | null>(null);
  const [liveMinimized, setLiveMinimized] = useState(false);
  const [modalMinimized, setModalMinimized] = useState(false);

  function handleLive(c: typeof DEFAULT_CFG & { jobId: string }, t: TokenData, asModal?: boolean) {
    if (asModal) {
      setModalLive({ cfg: c, token: t });
      setModalMinimized(false);
      return;
    }
    setCfg(c);
    setToken(t);
    setView("live");
    setLiveMinimized(false);
  }

  function handleStopLive() {
    setView("app");
    setLiveMinimized(false);
  }

  function handleStopModal() {
    setModalLive(null);
    setModalMinimized(false);
  }

  return (
    <div>
      <StyleInjector />
      {(view === "app" || liveMinimized) && (
        <BotApp onLive={handleLive} />
      )}
      {view === "live" && cfg && token && (
        <LiveDash
          cfg={cfg}
          token={token}
          onStop={handleStopLive}
          minimized={liveMinimized}
          onMinimize={function () { setLiveMinimized(true); }}
          onExpand={function () { setLiveMinimized(false); }}
        />
      )}
      {modalLive && (
        <LiveDash
          cfg={modalLive.cfg}
          token={modalLive.token}
          onStop={handleStopModal}
          minimized={modalMinimized}
          onMinimize={function () { setModalMinimized(true); }}
          onExpand={function () { setModalMinimized(false); }}
          overlay={!modalMinimized}
        />
      )}
    </div>
  );
}
